// src/components/layout/Layout.js - Updated with Sites Submenu and Settings
import React, { useState } from 'react';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DescriptionIcon from '@mui/icons-material/Description';
import ComputerIcon from '@mui/icons-material/Computer';
import CableIcon from '@mui/icons-material/Cable';
import SettingsIcon from '@mui/icons-material/Settings';
import { 
  AppBar, 
  Box, 
  Container, 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Toolbar, 
  Typography,
  Divider,
  IconButton,
  useMediaQuery,
  Tooltip,
  Menu,
  MenuItem,
  Avatar,
  Chip,
  Collapse
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PaymentIcon from '@mui/icons-material/Payment';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { useTheme } from '@mui/material/styles';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EvStationIcon from '@mui/icons-material/EvStation';
import BoltIcon from '@mui/icons-material/Bolt';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import CategoryIcon from '@mui/icons-material/Category';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// Import from our custom theme files
import { colors } from '../../theme/colorScheme';

// Width of the drawer when open and closed
const drawerWidthOpen = 240;
const drawerWidthClosed = 84;

// Sidebar colors - using our theme colors
const sidebarColors = {
  background: "rgba(255, 255, 255, 1)", // White background
  text: colors.text.primary,
  activeBackground: `${colors.primary.main}12`, // Primary color with 12% opacity
  activeText: colors.primary.main,
  iconDefault: colors.text.secondary,
  iconActive: colors.primary.main,
  divider: colors.divider,
  hoverBackground: "rgba(0, 0, 0, 0.04)"
};

const Layout = ({ children }) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, logout, isSuperAdmin, isAdmin, isDriver, activeChargerContext} = useAuth();
  
  // State to track if the drawer is open
  const [open, setOpen] = useState(!isMobile);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [sitesSubmenuOpen, setSitesSubmenuOpen] = useState(false);
  const [driverGroupsSubmenuOpen, setDriverGroupsSubmenuOpen] = useState(false);
  const [logsSubmenuOpen, setLogsSubmenuOpen] = useState(false);

  // Function to toggle drawer
  const toggleDrawer = () => {
    setOpen(!open);
  };

  // Function to toggle sites submenu
  const toggleSitesSubmenu = () => {
    setSitesSubmenuOpen(!sitesSubmenuOpen);
  };

  // Function to toggle driver groups submenu
  const toggleDriverGroupsSubmenu = () => {
    setDriverGroupsSubmenuOpen(!driverGroupsSubmenuOpen);
  };

  // Function to toggle logs submenu
  const toggleLogsSubmenu = () => {
    setLogsSubmenuOpen(!logsSubmenuOpen);
  };

  // Function to check if the current route matches the navigation item
  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  // Check if any sites-related route is active
  const isSitesActive = () => {
    return isActive('/sites') || isActive('/site-groups');
  };

  // Check if any driver groups-related route is active
  const isDriverGroupsActive = () => {
    return isActive('/drivers') || isActive('/driver_groups');
  };

  // Check if any logs-related route is active
  const isLogsActive = () => {
    return isActive('/system-logs') || isActive('/ocpp-logs');
  };

  // User menu handlers
  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = async () => {
    handleUserMenuClose();
    await logout();
  };

  // Get role icon
  const getRoleIcon = (role) => {
    switch (role) {
      case 'SuperAdmin':
        return <SupervisorAccountIcon fontSize="small" />;
      case 'Admin':
        return <AdminPanelSettingsIcon fontSize="small" />;
      case 'Driver':
        return <PersonIcon fontSize="small" />;
      default:
        return <AccountCircleIcon fontSize="small" />;
    }
  };

  // Get role color
  const getRoleColor = (role) => {
    switch (role) {
      case 'SuperAdmin':
        return 'error';
      case 'Admin':
        return 'warning';
      case 'Driver':
        return 'info';
      default:
        return 'default';
    }
  };

  const drawerWidth = open ? drawerWidthOpen : drawerWidthClosed;

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar 
        position="fixed" 
        sx={{ 
          width: '100%', // Full width
          zIndex: theme.zIndex.drawer + 1, // Above the drawer
          backgroundColor: colors.primary.main, // Primary green color from our theme
          height: 80, // Increased from default (~64px)
        }}
      >
        <Toolbar sx={{ minHeight: '80px !important' }}>
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
            <Typography 
              variant="h4" 
              noWrap 
              component="div" 
              fontWeight="bold"
              sx={{
                fontSize: { xs: '1.75rem', sm: '2.25rem' }, // Responsive font size
                letterSpacing: '0.5px'
              }}
            >
              GetWatted CSMS
            </Typography>
          </Box>
          {/* User Info and Menu */}
          <Box display="flex" alignItems="center" gap={2}>
            {/* User Role Chip */}
            <Chip
              icon={getRoleIcon(user?.role)}
              label={user?.role}
              color={getRoleColor(user?.role)}
              size="small"
              variant="outlined"
              sx={{ 
                color: 'white', 
                borderColor: 'rgba(255,255,255,0.5)',
                '& .MuiChip-icon': { color: 'white' }
              }}
            />
            
            {/* User Menu Button */}
            <Tooltip title="Account">
              <IconButton
                onClick={handleUserMenuOpen}
                sx={{ 
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                }}
              >
                <Avatar 
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    bgcolor: 'rgba(255,255,255,0.2)',
                    fontSize: '0.875rem'
                  }}
                >
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </Avatar>
              </IconButton>
            </Tooltip>

            {/* User Menu */}
            <Menu
              anchorEl={userMenuAnchor}
              open={Boolean(userMenuAnchor)}
              onClose={handleUserMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <Box sx={{ p: 2, minWidth: 200 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Signed in as
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {user?.first_name} {user?.last_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user?.email}
                </Typography>
                {user?.company_id && (
                  <Typography variant="caption" color="text.secondary">
                    Company ID: {user.company_id}
                  </Typography>
                )}
              </Box>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            overflowX: 'hidden',
            backgroundColor: sidebarColors.background,
            borderRight: `1px solid ${sidebarColors.divider}`,
            marginTop: '80px', // Start below the AppBar
            height: 'calc(100vh - 80px)', // Adjust height to account for AppBar
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }}
      >

        {/* Navigation Menu */}
        <List sx={{ px: 1 }}>
          {/* Dashboard - Only show for SuperAdmin and Admin */}
          {(isSuperAdmin() || isAdmin()) && (
            <ListItem 
              component={Link} 
              to="/"
              sx={{ 
                bgcolor: isActive('/') && !isActive('/companies') && !isActive('/sites') && 
                       !isActive('/site-groups') && !isActive('/chargers') && !isActive('/sessions')
                       ? sidebarColors.activeBackground : 'transparent',
                '&:hover': { bgcolor: sidebarColors.hoverBackground },
                px: 2.5, py: 1.5, mb: 0.5, borderRadius: '8px', mx: 0.8, textDecoration: 'none',
              }}
            >
              <Tooltip title={open ? "" : "Dashboard"} placement="right">
                <ListItemIcon sx={{ 
                  minWidth: 0, mr: open ? 2 : 'auto', justifyContent: 'center',
                  color: isActive('/') && !isActive('/companies') && !isActive('/sites') && 
                         !isActive('/site-groups') && !isActive('/chargers') && !isActive('/sessions')
                         ? sidebarColors.iconActive : sidebarColors.iconDefault
                }}>
                  <HomeIcon />
                </ListItemIcon>
              </Tooltip>
              {open && (
                <ListItemText 
                  primary="Dashboard" 
                  primaryTypographyProps={{ 
                    color: isActive('/') && !isActive('/companies') && !isActive('/sites') && 
                           !isActive('/site-groups') && !isActive('/chargers') && !isActive('/sessions')
                           ? sidebarColors.activeText : sidebarColors.text,
                    fontWeight: isActive('/') && !isActive('/companies') && !isActive('/sites') && 
                                !isActive('/site-groups') && !isActive('/chargers') && !isActive('/sessions')
                                ? 'bold' : 'normal'
                  }}
                />
              )}
            </ListItem>
          )}
          
          {/* Companies - Only show for SuperAdmin (NOT for Admin) */}
          {isSuperAdmin() && (
            <ListItem 
              component={Link} 
              to="/companies"
              sx={{ 
                bgcolor: isActive('/companies') ? sidebarColors.activeBackground : 'transparent',
                '&:hover': { bgcolor: sidebarColors.hoverBackground },
                px: 2.5, py: 1.5, mb: 0.5, borderRadius: '8px', mx: 0.8, textDecoration: 'none',
              }}
            >
              <Tooltip title={open ? "" : "Companies"} placement="right">
                <ListItemIcon sx={{ 
                  minWidth: 0, mr: open ? 2 : 'auto', justifyContent: 'center',
                  color: isActive('/companies') ? sidebarColors.iconActive : sidebarColors.iconDefault
                }}>
                  <BusinessIcon />
                </ListItemIcon>
              </Tooltip>
              {open && (
                <ListItemText 
                  primary="Companies" 
                  primaryTypographyProps={{ 
                    color: isActive('/companies') ? sidebarColors.activeText : sidebarColors.text,
                    fontWeight: isActive('/companies') ? 'bold' : 'normal'
                  }}
                />
              )}
            </ListItem>
          )}

          {/* Sites Menu with Submenu - Only show for SuperAdmin and Admin */}
          {(isSuperAdmin() || isAdmin()) && (
            <>
              <ListItem 
                onClick={open ? toggleSitesSubmenu : undefined}
                sx={{ 
                  bgcolor: isSitesActive() ? sidebarColors.activeBackground : 'transparent',
                  '&:hover': { bgcolor: sidebarColors.hoverBackground },
                  px: 2.5, py: 1.5, mb: 0.5, borderRadius: '8px', mx: 0.8,
                  cursor: open ? 'pointer' : 'default',
                }}
              >
                <Tooltip title={open ? "" : "Sites"} placement="right">
                  <ListItemIcon sx={{ 
                    minWidth: 0, mr: open ? 2 : 'auto', justifyContent: 'center',
                    color: isSitesActive() ? sidebarColors.iconActive : sidebarColors.iconDefault
                  }}>
                    <LocationOnIcon />
                  </ListItemIcon>
                </Tooltip>
                {open && (
                  <>
                    <ListItemText 
                        primary="Sites" 
                        primaryTypographyProps={{ 
                          color: isSitesActive() ? sidebarColors.activeText : sidebarColors.text,
                          fontWeight: isSitesActive() ? 'bold' : 'normal'
                        }}
                      />
                      <KeyboardArrowDownIcon 
                        sx={{ 
                          color: sidebarColors.iconDefault,
                          transform: sitesSubmenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease-in-out'
                        }} 
                      />
                    </>
                )}
              </ListItem>

              {/* Sites Submenu */}
              {open && (
                <Collapse in={sitesSubmenuOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {/* Groups Submenu Item */}
                    <ListItem 
                      component={Link} 
                      to="/site-groups"
                      sx={{ 
                        bgcolor: isActive('/site-groups') ? sidebarColors.activeBackground : 'transparent',
                        '&:hover': { bgcolor: sidebarColors.hoverBackground },
                        pl: 6, pr: 2.5, py: 1, mb: 0.5, borderRadius: '8px', mx: 0.8, textDecoration: 'none',
                      }}
                    >
                      <ListItemIcon sx={{ 
                        minWidth: 0, mr: 2, justifyContent: 'center',
                        color: isActive('/site-groups') ? sidebarColors.iconActive : sidebarColors.iconDefault
                      }}>
                        <CategoryIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Groups" 
                        primaryTypographyProps={{ 
                          color: isActive('/site-groups') ? sidebarColors.activeText : sidebarColors.text,
                          fontWeight: isActive('/site-groups') ? 'bold' : 'normal',
                          fontSize: '0.875rem'
                        }}
                      />
                    </ListItem>

                    {/* Sites Submenu Item */}
                    <ListItem 
                      component={Link} 
                      to="/sites"
                      sx={{ 
                        bgcolor: isActive('/sites') && !isActive('/site-groups') ? sidebarColors.activeBackground : 'transparent',
                        '&:hover': { bgcolor: sidebarColors.hoverBackground },
                        pl: 6, pr: 2.5, py: 1, mb: 0.5, borderRadius: '8px', mx: 0.8, textDecoration: 'none',
                      }}
                    >
                      <ListItemIcon sx={{ 
                        minWidth: 0, mr: 2, justifyContent: 'center',
                        color: isActive('/sites') && !isActive('/site-groups') ? sidebarColors.iconActive : sidebarColors.iconDefault
                      }}>
                        <LocationOnIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Sites" 
                        primaryTypographyProps={{ 
                          color: isActive('/sites') && !isActive('/site-groups') ? sidebarColors.activeText : sidebarColors.text,
                          fontWeight: isActive('/sites') && !isActive('/site-groups') ? 'bold' : 'normal',
                          fontSize: '0.875rem'
                        }}
                      />
                    </ListItem>
                  </List>
                </Collapse>
              )}
            </>
          )}
          
          {/* Driver Groups Menu with Submenu - Only show for SuperAdmin and Admin */}
          {(isSuperAdmin() || isAdmin()) && (
            <>
              <ListItem 
                onClick={open ? toggleDriverGroupsSubmenu : undefined}
                sx={{ 
                  bgcolor: isDriverGroupsActive() ? sidebarColors.activeBackground : 'transparent',
                  '&:hover': { bgcolor: sidebarColors.hoverBackground },
                  px: 2.5, py: 1.5, mb: 0.5, borderRadius: '8px', mx: 0.8,
                  cursor: open ? 'pointer' : 'default',
                }}
              >
                <Tooltip title={open ? "" : "Driver Groups"} placement="right">
                  <ListItemIcon sx={{ 
                    minWidth: 0, mr: open ? 2 : 'auto', justifyContent: 'center',
                    color: isDriverGroupsActive() ? sidebarColors.iconActive : sidebarColors.iconDefault
                  }}>
                    <GroupsIcon />
                  </ListItemIcon>
                </Tooltip>
                {open && (
                  <>
                    <ListItemText 
                      primary="Drivers" 
                      primaryTypographyProps={{ 
                        color: isDriverGroupsActive() ? sidebarColors.activeText : sidebarColors.text,
                        fontWeight: isDriverGroupsActive() ? 'bold' : 'normal'
                      }}
                    />
                    <KeyboardArrowDownIcon 
                        sx={{ 
                          color: sidebarColors.iconDefault,
                          transform: driverGroupsSubmenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease-in-out'
                        }} 
                      />
                  </>
                )}
              </ListItem>

              {/* Driver Groups Submenu */}
              {open && (
                <Collapse in={driverGroupsSubmenuOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {/* Groups Submenu Item */}
                    <ListItem 
                      component={Link} 
                      to="/driver_groups"
                      sx={{ 
                        bgcolor: isActive('/driver_groups') ? sidebarColors.activeBackground : 'transparent',
                        '&:hover': { bgcolor: sidebarColors.hoverBackground },
                        pl: 6, pr: 2.5, py: 1, mb: 0.5, borderRadius: '8px', mx: 0.8, textDecoration: 'none',
                      }}
                    >
                      <ListItemIcon sx={{ 
                        minWidth: 0, mr: 2, justifyContent: 'center',
                        color: isActive('/driver_groups') ? sidebarColors.iconActive : sidebarColors.iconDefault
                      }}>
                        <GroupsIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Groups" 
                        primaryTypographyProps={{ 
                          color: isActive('/driver_groups') ? sidebarColors.activeText : sidebarColors.text,
                          fontWeight: isActive('/driver_groups') ? 'bold' : 'normal',
                          fontSize: '0.875rem'
                        }}
                      />
                    </ListItem>

                    {/* Drivers Submenu Item */}
                    <ListItem 
                      component={Link} 
                      to="/drivers"
                      sx={{ 
                        bgcolor: isActive('/drivers') && !isActive('/driver_groups') ? sidebarColors.activeBackground : 'transparent',
                        '&:hover': { bgcolor: sidebarColors.hoverBackground },
                        pl: 6, pr: 2.5, py: 1, mb: 0.5, borderRadius: '8px', mx: 0.8, textDecoration: 'none',
                      }}
                    >
                      <ListItemIcon sx={{ 
                        minWidth: 0, mr: 2, justifyContent: 'center',
                        color: isActive('/drivers') && !isActive('/driver_groups') ? sidebarColors.iconActive : sidebarColors.iconDefault
                      }}>
                        <PersonIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Drivers" 
                        primaryTypographyProps={{ 
                          color: isActive('/drivers') && !isActive('/driver_groups') ? sidebarColors.activeText : sidebarColors.text,
                          fontWeight: isActive('/drivers') && !isActive('/driver_groups') ? 'bold' : 'normal',
                          fontSize: '0.875rem'
                        }}
                      />
                    </ListItem>
                  </List>
                </Collapse>
              )}
            </>
          )}

          {/* Chargers - Only show for SuperAdmin and Admin */}
          {(isSuperAdmin() || isAdmin()) && (
            <ListItem 
              component={Link} 
              to="/chargers"
              sx={{ 
                bgcolor: isActive('/chargers') ? sidebarColors.activeBackground : 'transparent',
                '&:hover': { bgcolor: sidebarColors.hoverBackground },
                px: 2.5, py: 1.5, mb: 0.5, borderRadius: '8px', mx: 0.8, textDecoration: 'none',
              }}
            >
              <Tooltip title={open ? "" : "Chargers"} placement="right">
                <ListItemIcon sx={{ 
                  minWidth: 0, mr: open ? 2 : 'auto', justifyContent: 'center',
                  color: isActive('/chargers') ? sidebarColors.iconActive : sidebarColors.iconDefault
                }}>
                  <EvStationIcon />
                </ListItemIcon>
              </Tooltip>
              {open && (
                <ListItemText 
                  primary="Chargers" 
                  primaryTypographyProps={{ 
                    color: isActive('/chargers') ? sidebarColors.activeText : sidebarColors.text,
                    fontWeight: isActive('/chargers') ? 'bold' : 'normal'
                  }}
                />
              )}
            </ListItem>
          )}
          
          {/* Sessions (Driver Only) - Only show for Driver */}
          {isDriver() && (
            <ListItem 
              component={Link} 
              to="/driver-sessions"
              sx={{ 
                bgcolor: isActive('/driver-sessions') ? sidebarColors.activeBackground : 'transparent',
                '&:hover': { bgcolor: sidebarColors.hoverBackground },
                px: 2.5, py: 1.5, mb: 0.5, borderRadius: '8px', mx: 0.8, textDecoration: 'none',
              }}
            >
              <Tooltip title={open ? "" : "Sessions"} placement="right">
                <ListItemIcon sx={{ 
                  minWidth: 0, mr: open ? 2 : 'auto', justifyContent: 'center',
                  color: isActive('/driver-sessions') ? sidebarColors.iconActive : sidebarColors.iconDefault
                }}>
                  <BoltIcon />
                </ListItemIcon>
              </Tooltip>
              {open && (
                <ListItemText 
                  primary="Sessions" 
                  primaryTypographyProps={{ 
                    color: isActive('/driver-sessions') ? sidebarColors.activeText : sidebarColors.text,
                    fontWeight: isActive('/driver-sessions') ? 'bold' : 'normal'
                  }}
                />
              )}
            </ListItem>
          )}

          {/* Payments (Driver Only) - Only show for Driver */}
          {isDriver() && (
            <ListItem 
              component={Link} 
              to="/driver-payments"
              sx={{ 
                bgcolor: isActive('/driver-payments') ? sidebarColors.activeBackground : 'transparent',
                '&:hover': { bgcolor: sidebarColors.hoverBackground },
                px: 2.5, py: 1.5, mb: 0.5, borderRadius: '8px', mx: 0.8, textDecoration: 'none',
              }}
            >
              <Tooltip title={open ? "" : "Payments"} placement="right">
                <ListItemIcon sx={{ 
                  minWidth: 0, mr: open ? 2 : 'auto', justifyContent: 'center',
                  color: isActive('/driver-payments') ? sidebarColors.iconActive : sidebarColors.iconDefault
                }}>
                  <PaymentIcon />
                </ListItemIcon>
              </Tooltip>
              {open && (
                <ListItemText 
                  primary="Payments" 
                  primaryTypographyProps={{ 
                    color: isActive('/driver-payments') ? sidebarColors.activeText : sidebarColors.text,
                    fontWeight: isActive('/driver-payments') ? 'bold' : 'normal'
                  }}
                />
              )}
            </ListItem>
          )}

          {/* Profile (Driver Only) - Only show for Driver */}
          {isDriver() && (
            <ListItem 
              component={Link} 
              to="/driver-profile"
              sx={{ 
                bgcolor: isActive('/driver-profile') ? sidebarColors.activeBackground : 'transparent',
                '&:hover': { bgcolor: sidebarColors.hoverBackground },
                px: 2.5, py: 1.5, mb: 0.5, borderRadius: '8px', mx: 0.8, textDecoration: 'none',
              }}
            >
              <Tooltip title={open ? "" : "Profile"} placement="right">
                <ListItemIcon sx={{ 
                  minWidth: 0, mr: open ? 2 : 'auto', justifyContent: 'center',
                  color: isActive('/driver-profile') ? sidebarColors.iconActive : sidebarColors.iconDefault
                }}>
                  <PersonIcon />
                </ListItemIcon>
              </Tooltip>
              {open && (
                <ListItemText 
                  primary="Profile" 
                  primaryTypographyProps={{ 
                    color: isActive('/driver-profile') ? sidebarColors.activeText : sidebarColors.text,
                    fontWeight: isActive('/driver-profile') ? 'bold' : 'normal'
                  }}
                />
              )}
            </ListItem>
          )}

          {/* NEW: Current Charger (Driver Only) - Only show when active charger context exists */}
          {isDriver() && activeChargerContext && (
            <ListItem 
              component={Link} 
              to={`/chargers-driver-page?charger_id=${activeChargerContext.charger_id}&company=${activeChargerContext.company_id}&site=${activeChargerContext.site_id}&connector=${activeChargerContext.connector_number}`}
              sx={{ 
                bgcolor: isActive('/chargers-driver-page') ? sidebarColors.activeBackground : 'transparent',
                '&:hover': { bgcolor: sidebarColors.hoverBackground },
                px: 2.5, py: 1.5, mb: 0.5, borderRadius: '8px', mx: 0.8, textDecoration: 'none',
              }}
            >
              <Tooltip title={open ? "" : "Current Charger"} placement="right">
                <ListItemIcon sx={{ 
                  minWidth: 0, mr: open ? 2 : 'auto', justifyContent: 'center',
                  color: isActive('/chargers-driver-page') ? sidebarColors.iconActive : sidebarColors.iconDefault
                }}>
                  <EvStationIcon />
                </ListItemIcon>
              </Tooltip>
              {open && (
                <ListItemText 
                  primary={`Charger: ${activeChargerContext.charger_name}`}
                  primaryTypographyProps={{ 
                    color: isActive('/chargers-driver-page') ? sidebarColors.activeText : sidebarColors.text,
                    fontWeight: isActive('/chargers-driver-page') ? 'bold' : 'normal'
                  }}
                />
              )}
            </ListItem>
          )}

          {/* Sessions (Admin/SuperAdmin) - Only show for SuperAdmin and Admin */}
          {/* Sessions (Admin/SuperAdmin) - Only show for SuperAdmin and Admin */}
          {(isSuperAdmin() || isAdmin()) && (
            <ListItem 
              component={Link} 
              to="/sessions"
              sx={{ 
                bgcolor: isActive('/sessions') ? sidebarColors.activeBackground : 'transparent',
                '&:hover': { bgcolor: sidebarColors.hoverBackground },
                px: 2.5, py: 1.5, mb: 0.5, borderRadius: '8px', mx: 0.8, textDecoration: 'none',
              }}
            >
              <Tooltip title={open ? "" : "Sessions"} placement="right">
                <ListItemIcon sx={{ 
                  minWidth: 0, mr: open ? 2 : 'auto', justifyContent: 'center',
                  color: isActive('/sessions') ? sidebarColors.iconActive : sidebarColors.iconDefault
                }}>
                  <BoltIcon />
                </ListItemIcon>
              </Tooltip>
              {open && (
                <ListItemText 
                  primary="Sessions" 
                  primaryTypographyProps={{ 
                    color: isActive('/sessions') ? sidebarColors.activeText : sidebarColors.text,
                    fontWeight: isActive('/sessions') ? 'bold' : 'normal'
                  }}
                />
              )}
            </ListItem>
          )}

          {/* Tariffs - Only show for SuperAdmin and Admin */}
          {(isSuperAdmin() || isAdmin()) && (
            <ListItem 
              component={Link} 
              to="/tariffs"
              sx={{ 
                bgcolor: isActive('/tariffs') ? sidebarColors.activeBackground : 'transparent',
                '&:hover': { bgcolor: sidebarColors.hoverBackground },
                px: 2.5, py: 1.5, mb: 0.5, borderRadius: '8px', mx: 0.8, textDecoration: 'none',
              }}
            >
              <Tooltip title={open ? "" : "Tariffs"} placement="right">
                <ListItemIcon sx={{ 
                  minWidth: 0, mr: open ? 2 : 'auto', justifyContent: 'center',
                  color: isActive('/tariffs') ? sidebarColors.iconActive : sidebarColors.iconDefault
                }}>
                  <AttachMoneyIcon />
                </ListItemIcon>
              </Tooltip>
              {open && (
                <ListItemText 
                  primary="Tariffs" 
                  primaryTypographyProps={{ 
                    color: isActive('/tariffs') ? sidebarColors.activeText : sidebarColors.text,
                    fontWeight: isActive('/tariffs') ? 'bold' : 'normal'
                  }}
                />
              )}
            </ListItem>
          )}

          {/* Payment - Only show for SuperAdmin and Admin */}
          {(isSuperAdmin() || isAdmin()) && (
            <ListItem 
              component={Link} 
              to="/payment"
              sx={{ 
                bgcolor: isActive('/payment') ? sidebarColors.activeBackground : 'transparent',
                '&:hover': { bgcolor: sidebarColors.hoverBackground },
                px: 2.5, py: 1.5, mb: 0.5, borderRadius: '8px', mx: 0.8, textDecoration: 'none',
              }}
            >
              <Tooltip title={open ? "" : "Payment"} placement="right">
                <ListItemIcon sx={{ 
                  minWidth: 0, mr: open ? 2 : 'auto', justifyContent: 'center',
                  color: isActive('/payment') ? sidebarColors.iconActive : sidebarColors.iconDefault
                }}>
                  <PaymentIcon />
                </ListItemIcon>
              </Tooltip>
              {open && (
                <ListItemText 
                  primary="Payment Activities" 
                  primaryTypographyProps={{ 
                    color: isActive('/payment') ? sidebarColors.activeText : sidebarColors.text,
                    fontWeight: isActive('/payment') ? 'bold' : 'normal'
                  }}
                />
              )}
            </ListItem>
          )}
        
          {/* Users - Only show for SuperAdmin and Admin */}
          {(isSuperAdmin() || isAdmin()) && (
            <ListItem 
              component={Link} 
              to="/users"
              sx={{ 
                bgcolor: isActive('/users') ? sidebarColors.activeBackground : 'transparent',
                '&:hover': { bgcolor: sidebarColors.hoverBackground },
                px: 2.5, py: 1.5, mb: 0.5, borderRadius: '8px', mx: 0.8, textDecoration: 'none',
              }}
            >
              <Tooltip title={open ? "" : "Users"} placement="right">
                <ListItemIcon sx={{ 
                  minWidth: 0, mr: open ? 2 : 'auto', justifyContent: 'center',
                  color: isActive('/users') ? sidebarColors.iconActive : sidebarColors.iconDefault
                }}>
                  <PersonIcon />
                </ListItemIcon>
              </Tooltip>
              {open && (
                <ListItemText 
                  primary="Users" 
                  primaryTypographyProps={{ 
                    color: isActive('/users') ? sidebarColors.activeText : sidebarColors.text,
                    fontWeight: isActive('/users') ? 'bold' : 'normal'
                  }}
                />
              )}
            </ListItem>
          )}

          {/* Logs Menu with Submenu - Only show for SuperAdmin and Admin */}
          {(isSuperAdmin() || isAdmin()) && (
            <>
              <ListItem 
                onClick={open ? toggleLogsSubmenu : undefined}
                sx={{ 
                  bgcolor: isLogsActive() ? sidebarColors.activeBackground : 'transparent',
                  '&:hover': { bgcolor: sidebarColors.hoverBackground },
                  px: 2.5, py: 1.5, mb: 0.5, borderRadius: '8px', mx: 0.8,
                  cursor: open ? 'pointer' : 'default',
                }}
              >
                <Tooltip title={open ? "" : "Logs"} placement="right">
                  <ListItemIcon sx={{ 
                    minWidth: 0, mr: open ? 2 : 'auto', justifyContent: 'center',
                    color: isLogsActive() ? sidebarColors.iconActive : sidebarColors.iconDefault
                  }}>
                    <DescriptionIcon />
                  </ListItemIcon>
                </Tooltip>
                {open && (
                  <>
                    <ListItemText 
                      primary="Logs" 
                      primaryTypographyProps={{ 
                        color: isLogsActive() ? sidebarColors.activeText : sidebarColors.text,
                        fontWeight: isLogsActive() ? 'bold' : 'normal'
                      }}
                    />
                    <KeyboardArrowDownIcon 
                      sx={{ 
                        color: sidebarColors.iconDefault,
                        transform: logsSubmenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease-in-out'
                      }} 
                    />
                  </>
                )}
              </ListItem>

              {/* Logs Submenu */}
              {open && (
                <Collapse in={logsSubmenuOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {/* System Logs Submenu Item */}
                    <ListItem 
                      component={Link} 
                      to="/system-logs"
                      sx={{ 
                        bgcolor: isActive('/system-logs') ? sidebarColors.activeBackground : 'transparent',
                        '&:hover': { bgcolor: sidebarColors.hoverBackground },
                        pl: 6, pr: 2.5, py: 1, mb: 0.5, borderRadius: '8px', mx: 0.8, textDecoration: 'none',
                      }}
                    >
                      <ListItemIcon sx={{ 
                        minWidth: 0, mr: 2, justifyContent: 'center',
                        color: isActive('/system-logs') ? sidebarColors.iconActive : sidebarColors.iconDefault
                      }}>
                        <ComputerIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="System" 
                        primaryTypographyProps={{ 
                          color: isActive('/system-logs') ? sidebarColors.activeText : sidebarColors.text,
                          fontWeight: isActive('/system-logs') ? 'bold' : 'normal',
                          fontSize: '0.875rem'
                        }}
                      />
                    </ListItem>

                    {/* OCPP Comm. Submenu Item */}
                    <ListItem 
                      component={Link} 
                      to="/ocpp-logs"
                      sx={{ 
                        bgcolor: isActive('/ocpp-logs') ? sidebarColors.activeBackground : 'transparent',
                        '&:hover': { bgcolor: sidebarColors.hoverBackground },
                        pl: 6, pr: 2.5, py: 1, mb: 0.5, borderRadius: '8px', mx: 0.8, textDecoration: 'none',
                      }}
                    >
                      <ListItemIcon sx={{ 
                        minWidth: 0, mr: 2, justifyContent: 'center',
                        color: isActive('/ocpp-logs') ? sidebarColors.iconActive : sidebarColors.iconDefault
                      }}>
                        <CableIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="OCPP Comm." 
                        primaryTypographyProps={{ 
                          color: isActive('/ocpp-logs') ? sidebarColors.activeText : sidebarColors.text,
                          fontWeight: isActive('/ocpp-logs') ? 'bold' : 'normal',
                          fontSize: '0.875rem'
                        }}
                      />
                    </ListItem>
                  </List>
                </Collapse>
              )}
            </>
          )}

          {/* Settings - Only show for SuperAdmin and Admin */}
          {(isSuperAdmin() || isAdmin()) && (
            <ListItem 
              onClick={() => {
                // Settings button click handler - currently does nothing as requested
                console.log('Settings clicked');
              }}
              sx={{ 
                bgcolor: 'transparent', // No active state since it doesn't navigate
                '&:hover': { bgcolor: sidebarColors.hoverBackground },
                px: 2.5, py: 1.5, mb: 0.5, borderRadius: '8px', mx: 0.8,
                cursor: 'pointer',
              }}
            >
              <Tooltip title={open ? "" : "Settings"} placement="right">
                <ListItemIcon sx={{ 
                  minWidth: 0, mr: open ? 2 : 'auto', justifyContent: 'center',
                  color: sidebarColors.iconDefault
                }}>
                  <SettingsIcon />
                </ListItemIcon>
              </Tooltip>
              {open && (
                <ListItemText 
                  primary="Settings" 
                  primaryTypographyProps={{ 
                    color: sidebarColors.text,
                    fontWeight: 'normal'
                  }}
                />
              )}
            </ListItem>
          )}

        </List>
      </Drawer>
      
      {/* Floating Toggle Button */}
      <IconButton 
        onClick={toggleDrawer}
        sx={{ 
          position: 'fixed',
          top: '50%',
          left: open ? drawerWidthOpen - 20 : drawerWidthClosed - 20,
          transform: 'translateY(-50%)',
          zIndex: theme.zIndex.drawer + 2,
          width: 40,
          height: 40,
          backgroundColor: colors.primary.main,
          color: 'white',
          border: '2px solid white',
          borderRadius: '50%',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': { 
            backgroundColor: colors.primary.dark,
            transform: 'translateY(-50%) scale(1.1)',
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)'
          },
          '&:active': {
            transform: 'translateY(-50%) scale(0.95)'
          }
        }}
      >
        {open ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
      </IconButton>
      
      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 2, // Reduced padding
          width: `calc(100% - ${drawerWidth}px)`,
          ml: 0, // No additional margin left
          mt: 10, // Increased to account for the larger AppBar height
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Container maxWidth="xl"> {/* Increased from 'lg' to 'xl' for wider content */}
          {children}
        </Container>
      </Box>
    </Box>
  );
};

export default Layout;